import { useFindMany } from "@gadgetinc/react";
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import KeyValue from "../../../externalComponents/KeyValue";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Tooltip } from "../../../externalComponents/Tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { Text } from "../../../externalComponents/tailwind/text";
import { formatDateTime } from "../../../functions/date-utils";
import { campaignTypes } from "./campaign-types.const";
import { messagesStatuses } from "./message-statuses.const";

export default function ContactLastInteractions({
  contactId,
  customerId,
  isMobile,
}) {
  const [lastInteractions, setLastInteractions] = useState({
    conversationsIds: null,
    messages: null,
    rows: [],
  });
  const [campaignCache, setCampaignCache] = useState({});
  const NUM_ON_PAGE = 20;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });

  const formatMessage = (message) => {
    const { sentAt, readAt, deliveredAt, failedAt } = message;
    const dateValue = sentAt ? sentAt : failedAt;
    return {
      ...message,
      ...campaignCache[message.campaignId],
      key: `last-interaction-${message.id}`,
      date: formatDateTime(
        dateValue,
        isMobile ? "shortDate" : "shortTimeLongDate",
      ),
      status: readAt
        ? "read"
        : deliveredAt
          ? "delivered"
          : failedAt
            ? "failed"
            : "sent",
    };
  };

  useEffect(() => {
    setLastInteractions({ messages: null, conversationsIds: null, rows: [] });

    const fetchConversations = async () =>
      api.conversation.findMany({
        filter: {
          contactId: { equals: contactId },
          customerId: { equals: customerId },
          isArchived: { notEquals: true },
        },
        select: { id: true },
      });

    fetchConversations().then((conversations) => {
      const conversationsIds = conversations?.map(({ id }) => id);
      setLastInteractions((_prev) => ({ ..._prev, conversationsIds }));
    });
  }, [contactId, customerId]);

  const [messageRecords] = useFindMany(api.message, {
    ...cursor,
    filter: {
      conversation: {
        id: {
          in: lastInteractions.conversationsIds?.length
            ? lastInteractions.conversationsIds
            : [0],
        },
      },
      customerId: { equals: customerId },
      direction: { equals: "outbound" },
      campaignId: { notEquals: null },
      OR: [
        { deliveredAt: { notEquals: null } },
        { failedAt: { notEquals: null } },
      ],
    },
    select: {
      id: true,
      campaignId: true,
      sentAt: true,
      readAt: true,
      deliveredAt: true,
      failedAt: true,
      failedReason: true,
    },
    sort: {
      createdAt: "Descending",
    },
  });

  const [campaignRecords] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customerId },
      id: {
        in: [
          ...new Set(
            (messageRecords.data || []).map(({ campaignId }) => campaignId),
          ),
        ],
      },
    },
    select: {
      id: true,
      name: true,
      trigger: true,
    },
  });

  useEffect(() => {
    if (campaignRecords.fetching) {
      return;
    }
    campaignRecords.data?.forEach(({ id, name, trigger }) => {
      campaignCache[id] = {
        campaignName: name,
        type: trigger === "api" ? "automation" : "campaign",
        redirectHref:
          trigger === "api"
            ? `/automations/edit/${id}`
            : `/campaigns/edit/${id}`,
      };
    });
    setCampaignCache({ ...campaignCache });
    setLastInteractions((_prev) => {
      return {
        ..._prev,
        rows: _prev.rows.map(formatMessage),
      };
    });
  }, [campaignRecords.data, campaignRecords.fetching]);

  useEffect(() => {
    setLastInteractions((_prev) => {
      const alreadyThere = new Set(_prev.rows.map(({ id }) => id));
      const rows = (messageRecords.data || [])
        .filter((m) => !alreadyThere.has(m.id))
        .map(formatMessage);

      return {
        ..._prev,
        messages: messageRecords.data?.map(formatMessage),
        rows: [..._prev.rows, ...rows],
      };
    });
  }, [messageRecords.data, campaignCache]);

  const handleScroll = useCallback(
    (scrollRef) => {
      const el = scrollRef.current;
      if (
        !el ||
        messageRecords.fetching ||
        !messageRecords.data.hasNextPage ||
        !messageRecords.data.endCursor
      ) {
        return;
      }

      const reachedBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
      if (reachedBottom) {
        setCursor({ first: NUM_ON_PAGE, after: messageRecords.data.endCursor });
      }
    },
    [messageRecords.data, messageRecords.fetching],
  );

  return (
    <OverflowFrame handleScroll={handleScroll} isMobile={isMobile}>
      <Text className="text-lg font-bold mb-3">Last interactions</Text>
      <Table className="w-full md:table-fixed">
        <TableHead>
          <TableRow>
            <TableHeader>Date</TableHeader>
            {!isMobile && <TableHeader>Type</TableHeader>}
            <TableHeader>Name</TableHeader>
            {!isMobile && <TableHeader>Status</TableHeader>}
          </TableRow>
        </TableHead>

        <TableBody>
          {lastInteractions.rows?.length
            ? lastInteractions.rows.map((row, index) => (
                <TableRow
                  key={row.key}
                  {...(!isMobile && { href: row.redirectHref })}
                >
                  <TableCell>{row.date}</TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Badge
                        color={campaignTypes[row.type]}
                        className="capitalize"
                      >
                        {row.type}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell className=" max-w-[12rem] md:max-w-[20rem] w-[20%] break-words">
                    <KeyValue
                      index={`campaign-name-${index}`}
                      isRaw={true}
                      value={row.campaignName}
                      className="break-words"
                    />
                    {isMobile && (
                      <div className="inline-flex items-center gap-1">
                        <Badge
                          color={messagesStatuses[row.status]}
                          className="capitalize"
                        >
                          {row.status}
                        </Badge>
                        {row.status === "failed" && row.failedReason && (
                          <Tooltip
                            size="4"
                            color="red-600"
                            content={<div>{row.failedReason}</div>}
                          />
                        )}
                      </div>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <div className="inline-flex items-center gap-1">
                        <Badge
                          color={messagesStatuses[row.status]}
                          className="capitalize"
                        >
                          {row.status}
                        </Badge>
                        {row.status === "failed" && row.failedReason && (
                          <Tooltip
                            size="4"
                            color="red-600"
                            content={<div>{row.failedReason}</div>}
                          />
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            : !messageRecords.fetching && (
                <TableRow key="no-results">
                  <TableCell colSpan={4} className="py-4">
                    <div className="flex justify-center">No data found</div>
                  </TableCell>
                </TableRow>
              )}

          {messageRecords.fetching && (
            <TableRow key="loading">
              <TableCell colSpan={4} className="py-4">
                <div className="flex justify-center">
                  <LoadingSpinner />
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </OverflowFrame>
  );
}
